import * as React from 'react'
import {Suspense} from 'react'
import {usePreloadedQuery} from 'react-relay'
import type {PreloadedQuery} from 'react-relay/relay-hooks/EntryPointTypes'

import {getOverviewHref} from '../../../routes'
import {getIsSakuraUI} from '../../../selectors'
import type {BuildTypeId} from '../../../types'
import {stringifyId} from '../../../types'
import {truthy} from '../../../utils/guards'
import {objectToQuery} from '../../../utils/queryParams'
import {resolveRelative} from '../../../utils/url'
import Link from '../Link/Link'
import RouterLinkWrapper from '../RouterLinkWrapper/RouterLinkWrapper'

import type {PermalinksQuery} from './__generated__/PermalinksQuery.graphql'
import {finishedLink, pinnedLink, successfulLink} from './Permalinks.consts'
import {permalinksQuery} from './Permalinks.queries'

import styles from './Permalinks.css'

import {useAppSelector} from 'src/hooks/react-redux'

type BaseProps = {
  buildTypeId: BuildTypeId
  branch?: string
}

type Props = BaseProps & {
  queryRef: PreloadedQuery<PermalinksQuery>
}

function Permalinks({buildTypeId, branch, queryRef}: Props) {
  const isSakuraUI = useAppSelector(getIsSakuraUI)

  const generateLink = (type: string) =>
    isSakuraUI
      ? `${getOverviewHref({
          buildTypeId,
          buildId: type,
        })}?${objectToQuery({
          branch,
        })}`
      : resolveRelative('/viewLog.html', {
          buildTypeId: stringifyId(buildTypeId),
          buildId: type,
          buildBranch: branch,
        })

  const {pinned, successful, finished} = usePreloadedQuery(permalinksQuery, queryRef)

  const displayedLinks = [
    pinned?.count && pinnedLink,
    successful?.count && successfulLink,
    finished?.count && finishedLink,
  ].filter(truthy)

  return displayedLinks.length > 0 ? (
    <div className={styles.content}>
      {displayedLinks.map(({title, type}, i) => (
        <React.Fragment key={type}>
          {i > 0 && ', '}
          <RouterLinkWrapper>
            <Link href={generateLink(type)}>
              {i === 0 && 'last '}
              {title}
              {i === displayedLinks.length - 1 && ' build'}
            </Link>
          </RouterLinkWrapper>
        </React.Fragment>
      ))}
    </div>
  ) : null
}

type ContainerProps = BaseProps & {
  queryRef?: PreloadedQuery<PermalinksQuery>
  seqKey?: string
}

export default function PermalinksContainer({seqKey, queryRef, ...restProps}: ContainerProps) {
  return (
    <Suspense fallback="">{queryRef && <Permalinks queryRef={queryRef} {...restProps} />}</Suspense>
  )
}
